import { FC } from 'react';
import { useTranslation, EI18nLanguageUI } from '@packages/utils';
import { useTheme } from 'styled-components';
import { Modal } from 'components/Base/Modal';
import { Row, Col } from 'components/Base/Grid';
import SuccessSmile from 'assets/images/SuccessSmile.png';
import { Image } from 'components/Base/Image';
import { Title, Paragraph, Text } from 'components/Base/Typography';
import { ERegistrantStatus } from 'data/types/events.types';
import { TSuccessModalProps } from './SuccessModal.types';
import { registrSuccessModalKey } from '../../RegistrationModal.data';

const SuccessModal: FC<TSuccessModalProps> = ({
  openSuccessModal,
  setOpenSuccessModal,
}) => {
  const theme = useTheme();
  const { emails, open, registrantStatus, eventType, lng } = openSuccessModal;
  const { t } = useTranslation({ lng });
  const handleClose = () => {
    setOpenSuccessModal({
      open: false,
      emails: [],
      registrantStatus: undefined,
      eventType: undefined,
      lng: EI18nLanguageUI.ENGLISH,
    });
  };

  const isRegisteredInWaitList =
    registrantStatus === ERegistrantStatus.WAIT_LIST;

  const congratsText = t(
    isRegisteredInWaitList
      ? `${registrSuccessModalKey}.WAITLIST_MESSAGE`
      : `${registrSuccessModalKey}.REGISTER_MESSAGE`,
  );

  const formatEmails = (email: string[]): string => {
    if (email.length === 1) return email[0];
    if (email.length === 2) return `${email[0]} and ${email[1]}`;
    return `${email.slice(0, -1).join(', ')} and ${email[email.length - 1]}`;
  };

  const getEventTypeText = (typeKey: string): string => {
    const primaryKey = `enums.event-type.${typeKey}`;
    const standaloneKey = `enums.event-standalone-type.${typeKey}`;
    const workshopKey = `enums.workshop-type.${typeKey}`;

    const primary = t(primaryKey);
    if (primary !== primaryKey) return primary;

    const standalone = t(standaloneKey);
    if (standalone !== standaloneKey) return standalone;

    const workshop = t(workshopKey);
    if (workshop !== workshopKey) return workshop;

    return typeKey;
  };

  return (
    <Modal
      open={open}
      okButtonProps={{ style: { display: 'none' } }}
      cancelButtonProps={{ style: { display: 'none' } }}
      onCancel={handleClose}
      width={360}
      data-cy="success-modal"
    >
      <Row justify="center" className="text-center">
        <Image src={SuccessSmile} height={60} alt="success smile" />
        <Col span={24}>
          <Title level={3}>
            {t(`${registrSuccessModalKey}.CONGRATULATIONS_TITLE`)}
          </Title>
        </Col>

        <Paragraph
          fontSize={theme.fontSizeLg}
          fontWeight={theme.fontWeightRegular}
          className="text-center"
        >
          {congratsText}&nbsp;
        </Paragraph>

        {isRegisteredInWaitList && eventType && (
          <Paragraph
            fontSize={theme.fontSizeLg}
            fontWeight={theme.fontWeightBold}
            className="text-center"
          >
            {getEventTypeText(eventType)}
          </Paragraph>
        )}

        {emails && emails.length > 0 && (
          <Paragraph fontSize={theme.fontSizeMd}>
            {t(`${registrSuccessModalKey}.WAILTLIST_DESCRIPTION`)}{' '}
            <Text fontSize={theme.fontSizeMd} color={theme.colorPrimaryDefault}>
              {formatEmails(emails)}
            </Text>
          </Paragraph>
        )}
      </Row>
    </Modal>
  );
};

export default SuccessModal;
